import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { captureException } from "@sentry/react";
import axios from "axios";
import { toast } from "react-toastify";
import { type VirtuosoGridHandle, type ListRange } from "react-virtuoso";
import { listDatasetImageCrops, type DatasetImage } from "../api";
import t from "../translations";
import { useDatasetDetail } from "./DatasetDetailContext";
import { parseCropFilename } from "./cropImage";

// CDN calls per load batch — bounds parallelism.
const BATCH_SIZE = 20;
// Images loaded on each side of the target when jumping to a non-loaded crop.
const RECENTER_RADIUS = 40;

export interface Crop {
  // Unique key — multiple crops per image.
  key: string;
  imageId: number;
  region: string;
  projectId: number;
  path: string;
  isAugmented: boolean;
  cropIndex: number | null;
}

function buildCrop(image: DatasetImage, path: string): Crop {
  const { isAugmented, cropIndex } = parseCropFilename(path);
  return {
    key: `${image.id}:${path}`,
    imageId: image.id,
    region: image.region,
    projectId: image.project_id,
    path,
    isAugmented,
    cropIndex,
  };
}

// Owns the lazily-loaded crop window backing the All Crops / per-image grids.
// Crops load in a movable [start, end) slice of `sourceImages`: scrolling
// extends it in either direction, and `goToActive` recenters it on a target
// without loading everything in between.
export function useCropWindow({
  sourceImages,
  activeImageId,
}: {
  sourceImages: DatasetImage[];
  activeImageId: number;
}) {
  const { datasetId, kind, isProjectAccessible } = useDatasetDetail();

  const [crops, setCrops] = useState<Crop[]>([]);
  const [fetching, setFetching] = useState(false);
  const [forwardExhausted, setForwardExhausted] = useState(false);
  const [seeking, setSeeking] = useState(false);
  // null = not yet fetched; bool = mapped value from the first image fetched.
  // Used by single-image callers to distinguish "no mapping" from "empty mapping".
  const [imageMapped, setImageMapped] = useState<boolean | null>(null);

  const gridRef = useRef<VirtuosoGridHandle>(null);
  // Mirror of `crops` for stale-free reads inside async loaders.
  const cropsRef = useRef<Crop[]>([]);
  // The [start, end) slice of sourceImages that `crops` currently covers.
  const startRef = useRef(0);
  const endRef = useRef(0);
  const loadingRef = useRef(false); // a load in any direction is in flight
  const sessionRef = useRef(0); // bumped on reset/recenter to drop stale batches
  const abortRef = useRef<AbortController | null>(null);
  const toastedErrorRef = useRef(false); // one error toast per session
  // Latest visible item range — anchors the viewport when prepending.
  const rangeRef = useRef<ListRange>({ startIndex: 0, endIndex: 0 });
  // Scroll to run once, in a layout effect, after the next `crops` change.
  // Anchored by crop key so it survives the array changing under it.
  const pendingScrollRef = useRef<{
    key: string;
    align: "start" | "center";
  } | null>(null);

  // Single writer for crops so the ref mirror never drifts from the state.
  const commitCrops = useCallback((next: Crop[]) => {
    cropsRef.current = next;
    setCrops(next);
  }, []);

  // Reset the window and abort in-flight requests when the source list changes.
  useEffect(() => {
    sessionRef.current += 1;
    startRef.current = 0;
    endRef.current = 0;
    loadingRef.current = false;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    toastedErrorRef.current = false;
    pendingScrollRef.current = null;
    commitCrops([]);
    setFetching(false);
    setForwardExhausted(false);
    setSeeking(false);
    setImageMapped(null);
    // Abort whatever load is active at teardown (recenter may have swapped it).
    return () => abortRef.current?.abort();
  }, [sourceImages, commitCrops]);

  // Fetch crops for sourceImages[from, to) in order. Per-image failures degrade
  // to zero crops (with a single toast) so one bad image can't stall the grid.
  const fetchRange = useCallback(
    async (from: number, to: number, signal?: AbortSignal): Promise<{ crops: Crop[]; firstMapped: boolean | null }> => {
      const results = await Promise.all(
        sourceImages.slice(from, to).map(async (image) => {
          if (!isProjectAccessible(image.project_id)) {
            return { image, paths: [] as string[], mapped: null as boolean | null };
          }
          try {
            const { paths, mapped } = await listDatasetImageCrops(
              image.region,
              datasetId,
              image.id,
              kind,
              signal,
            );
            return { image, paths, mapped };
          } catch (error) {
            if (!axios.isCancel(error)) {
              captureException(error, { tags: { feature: "cropGrid.fetch" } });
              if (!toastedErrorRef.current) {
                toastedErrorRef.current = true;
                toast.error(t.FailedToLoadImages);
              }
            }
            return { image, paths: [] as string[], mapped: null as boolean | null };
          }
        }),
      );
      const crops = results.flatMap(({ image, paths }) =>
        paths.map((path) => buildCrop(image, path)),
      );
      const firstMapped = results.length > 0 ? results[0].mapped : null;
      return { crops, firstMapped };
    },
    [sourceImages, datasetId, kind, isProjectAccessible],
  );

  // Extend the window by a batch in one direction, skipping zero-crop images so
  // the grid never appears stuck. A prepend queues a key-anchored re-scroll so
  // the viewport stays put.
  const extendWindow = useCallback(
    async (direction: "forward" | "backward") => {
      // A queued scroll means a window shift is mid-flight — don't race it.
      if (loadingRef.current || pendingScrollRef.current) return;
      const forward = direction === "forward";
      if (forward ? endRef.current >= sourceImages.length : startRef.current <= 0) {
        return;
      }
      loadingRef.current = true;
      if (forward) setFetching(true);
      const session = sessionRef.current;
      const signal = abortRef.current?.signal;
      try {
        let changed = false;
        while (
          !changed &&
          session === sessionRef.current &&
          (forward ? endRef.current < sourceImages.length : startRef.current > 0)
        ) {
          const from = forward
            ? endRef.current
            : Math.max(0, startRef.current - BATCH_SIZE);
          const to = forward
            ? Math.min(endRef.current + BATCH_SIZE, sourceImages.length)
            : startRef.current;
          const { crops: batch, firstMapped } = await fetchRange(from, to, signal);
          if (session !== sessionRef.current) return;
          if (forward) endRef.current = to;
          else startRef.current = from;
          if (firstMapped !== null) setImageMapped(firstMapped);
          if (batch.length === 0) continue;
          changed = true;

          const prev = cropsRef.current;
          if (forward) {
            commitCrops([...prev, ...batch]);
          } else {
            // Pin the previously-top crop by key so the prepend doesn't jump.
            const anchor =
              prev[Math.min(rangeRef.current.startIndex, prev.length - 1)];
            pendingScrollRef.current = anchor
              ? { key: anchor.key, align: "start" }
              : null;
            commitCrops([...batch, ...prev]);
          }
        }
        if (
          forward &&
          session === sessionRef.current &&
          endRef.current >= sourceImages.length
        ) {
          setForwardExhausted(true);
        }
      } finally {
        if (session === sessionRef.current) {
          loadingRef.current = false;
          if (forward) setFetching(false);
        }
      }
    },
    [sourceImages, fetchRange, commitCrops],
  );

  // Replace the window with one centred on `targetIndex` and scroll the target
  // crop into view — loads only the surrounding images, not everything before.
  const recenterOn = useCallback(
    async (targetIndex: number) => {
      sessionRef.current += 1;
      const session = sessionRef.current;
      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;
      loadingRef.current = true; // hold the lock so extends can't race
      const from = Math.max(0, targetIndex - RECENTER_RADIUS);
      const to = Math.min(sourceImages.length, targetIndex + RECENTER_RADIUS + 1);
      startRef.current = from;
      endRef.current = to;
      setForwardExhausted(to >= sourceImages.length);
      setSeeking(true);
      setFetching(true);
      try {
        const { crops: windowCrops, firstMapped } = await fetchRange(from, to, controller.signal);
        if (session !== sessionRef.current) return;
        if (firstMapped !== null) setImageMapped(firstMapped);
        const target = windowCrops.find((crop) => crop.imageId === activeImageId);
        if (target) pendingScrollRef.current = { key: target.key, align: "center" };
        else toast.info(t.NoCropsForCurrentImage);
        commitCrops(windowCrops);
      } finally {
        if (session === sessionRef.current) {
          loadingRef.current = false;
          setFetching(false);
          setSeeking(false);
        }
      }
    },
    [sourceImages, fetchRange, activeImageId, commitCrops],
  );

  // Prime the first batch — VirtuosoGrid only fires endReached on scroll.
  useEffect(() => {
    if (
      sourceImages.length > 0 &&
      crops.length === 0 &&
      !loadingRef.current &&
      !pendingScrollRef.current &&
      !forwardExhausted
    ) {
      extendWindow("forward");
    }
  }, [sourceImages, crops.length, forwardExhausted, extendWindow]);

  // Run the deferred scroll once the shifted crops are committed; resolving the
  // anchor key against the committed array keeps it correct.
  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    const index = crops.findIndex((crop) => crop.key === pending.key);
    if (index >= 0) gridRef.current?.scrollToIndex({ index, align: pending.align });
  }, [crops]);

  // First loaded crop of the active image, or -1 if none is loaded.
  const activeCropIndex = useMemo(
    () => crops.findIndex((crop) => crop.imageId === activeImageId),
    [crops, activeImageId],
  );

  // Scroll to the active crop if it's loaded; otherwise recenter the window on
  // it (loading the surrounding crops) and scroll there.
  const goToActive = useCallback(() => {
    if (activeCropIndex >= 0) {
      gridRef.current?.scrollToIndex({
        index: activeCropIndex,
        align: "center",
        behavior: "smooth",
      });
      return;
    }
    const targetIndex = sourceImages.findIndex(
      (image) => image.id === activeImageId,
    );
    if (targetIndex >= 0) recenterOn(targetIndex);
  }, [activeCropIndex, sourceImages, activeImageId, recenterOn]);

  const onEndReached = useCallback(() => extendWindow("forward"), [extendWindow]);
  const onStartReached = useCallback(
    () => extendWindow("backward"),
    [extendWindow],
  );
  const onRangeChanged = useCallback((range: ListRange) => {
    rangeRef.current = range;
  }, []);

  return {
    crops,
    fetching,
    forwardExhausted,
    seeking,
    imageMapped,
    gridRef,
    goToActive,
    onEndReached,
    onStartReached,
    onRangeChanged,
  };
}
